import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { token } = useAuth();
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId || attempts >= 8) {
      if (attempts >= 8) setStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        fetchCart();
        return;
      }
      if (res.data.status === 'expired') {
        setStatus('failed');
        return;
      }
      setAttempts(prev => prev + 1);
    } catch {
      setAttempts(prev => prev + 1);
    }
  }, [sessionId, attempts, token, fetchCart]);

  useEffect(() => {
    if (status === 'checking' && attempts < 8) {
      const timer = setTimeout(pollStatus, attempts === 0 ? 500 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, pollStatus]);

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        {status === 'checking' && (
          <div className="animate-fade-in">
            <Loader2 size={48} className="animate-spin text-warmgold mx-auto mb-6" />
            <h1 className="font-heading text-2xl text-espresso mb-2">Processing Payment</h1>
            <p className="text-text-muted">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-sage" />
            </div>
            <h1 data-testid="checkout-success-title" className="font-heading text-3xl text-espresso mb-3">
              Order Confirmed
            </h1>
            <p className="text-text-muted mb-8">
              Your order has been placed successfully. You'll receive a confirmation via SMS.
            </p>
            <div className="space-y-3">
              <Button
                data-testid="view-orders-btn"
                onClick={() => navigate('/account')}
                className="w-full bg-espresso hover:bg-espresso-light text-white rounded-full h-12"
              >
                View My Orders
              </Button>
              <Button
                data-testid="back-home-btn"
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full border-warm-border text-espresso rounded-full h-12 hover:bg-sand"
              >
                Back to Home
              </Button>
            </div>
          </div>
        )}

        {(status === 'failed' || status === 'timeout') && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-warm-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-warm-error" />
            </div>
            <h1 className="font-heading text-2xl text-espresso mb-3">Payment Issue</h1>
            <p className="text-text-muted mb-8">
              {status === 'timeout' ? 'Payment verification timed out. Please check your orders.' : 'Your payment could not be processed. Please try again.'}
            </p>
            <Button
              data-testid="retry-btn"
              onClick={() => navigate('/cart')}
              className="w-full bg-espresso hover:bg-espresso-light text-white rounded-full h-12"
            >
              Return to Cart
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
